"use client";

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  fetchFirms,
  fetchFeaturedFirms,
  fetchFirmById,
  fetchFirmFilterOptions,
} from "@/lib/queries/firms";
import { LIVE_FIRM_DATA_OPTIONS } from "@/lib/queryOptions";

export function useFirms(
  page = 1,
  limit = 10,
  filter = "ratings",
  search = "",
  advancedFilters = null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["firms", page, limit, filter, search, advancedFilters],
    queryFn: () => fetchFirms(page, limit, filter, search, advancedFilters),
    placeholderData: keepPreviousData,
    ...LIVE_FIRM_DATA_OPTIONS,
    enabled,
  });
}

/** Paginated firm list that accumulates pages for infinite scroll. */
export function useInfiniteFirms(
  limit = 10,
  filter = "ratings",
  search = "",
  advancedFilters = null,
  enabled = true,
) {
  // Serialize filters so a new object with the same values does not reset the
  // infinite query (which drops already-fetched pages and looks like "Network
  // called page 2 but the list did not grow").
  const filtersKey = JSON.stringify(advancedFilters ?? {});

  return useInfiniteQuery({
    queryKey: ["firms", "infinite", limit, filter, search, filtersKey],
    queryFn: ({ pageParam }) =>
      fetchFirms(pageParam, limit, filter, search, advancedFilters),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = Number(lastPage?.pagination?.page) || 1;
      const totalPages = Number(lastPage?.pagination?.totalPages) || 1;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: LIVE_FIRM_DATA_OPTIONS.staleTime,
    gcTime: LIVE_FIRM_DATA_OPTIONS.gcTime,
    // Infinite lists must not refetch-all-pages on focus/mount — that races
    // with fetchNextPage and can leave the UI on page 1 while Network shows 2/3.
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useFirmFilterOptions() {
  return useQuery({
    queryKey: ["firmFilterOptions"],
    queryFn: fetchFirmFilterOptions,
    ...LIVE_FIRM_DATA_OPTIONS,
  });
}

export function useFeaturedFirms() {
  return useQuery({
    queryKey: ["featuredFirms"],
    queryFn: fetchFeaturedFirms,
    ...LIVE_FIRM_DATA_OPTIONS,
  });
}

// ✅ Single firm by slug (legacy UUID URLs are 301'd to the slug by middleware)
//
// `initialData` is the firm fetched on the server by the page's server component.
// Passing it is what makes the page server-render: a client component does render on
// the server, but without seeded data React Query has nothing during SSR, so only the
// loading skeleton reached the HTML — which left firm pages with no crawlable content
// and no H1. With initialData the full page is in the first response.
// See docs/SEO-REMAINING-WORK.md.
export function useFirmById(id, initialData) {
  return useQuery({
    queryKey: ["firm", id],
    queryFn: () => fetchFirmById(id),
    enabled: !!id, // only fetch if id exists
    ...LIVE_FIRM_DATA_OPTIONS,
    ...(initialData ? { initialData } : {}),
  });
}
